import React, { useMemo, useState, useEffect  } from "react";
import { Form, Select, Collapse } from "antd";
import FilterContainer, { CoreFiltersBaseProps } from "../../components/FilterContainer";
import { useGetAllUsersQuery } from "../../../../store/services/api/user-api";
import { useGetAllBuildingsQuery } from "../../../../store/services/api/building-api";
import { useGetAllAssetTypesQuery } from "../../../../store/services/api/asset-type-api";
import { WorkOrderStatus, WorkOrderType } from "../../../../store/data/entities/work-order";

const { Panel } = Collapse;

export default function DashboardViewFilters({ filter, onSubmit }: CoreFiltersBaseProps) {
  const { data: buildings } = useGetAllBuildingsQuery(null);
  const selectedBuildingIds: string[] = filter.getFilter("buildingId") || [];

  const { data: allUsers } = useGetAllUsersQuery();
  const buildingLeads = useMemo(() => allUsers?.filter(u => u.role === "BuildingLead") || [], [allUsers]);
  const technicians = useMemo(() => allUsers?.filter(u => u.role === "Technician") || [], [allUsers]);

  const { data: assetTypes } = useGetAllAssetTypesQuery(null);
  const selectedAssetTypeIds: string[] = filter.getFilter("assetTypeId") || [];


  const [isSubmitting, setIsSubmitting] = useState(false);


  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(); 
    } finally {
      setTimeout(() => setIsSubmitting(false), 2000);
    }
  };

  useEffect(() => {
    setIsSubmitting(false);
  }, [buildings, allUsers, assetTypes]);

  return (
    <FilterContainer onResetFilters={filter.resetFilters} onSubmit={handleSubmit} isSubmitting={isSubmitting} >
      <Collapse defaultActiveKey={["dates", "buildingAsset", "statusType", "assignment"]} ghost>
        
        <Panel header="Building / Asset Type" key="buildingAsset">
          <Form layout="vertical">
            <Form.Item label="Building">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedBuildingIds}
                onChange={val => filter.setFilter("buildingId", val)}
                options={buildings?.map(b => ({ label: b.name, value: b.id }))}
              />
            </Form.Item>

            <Form.Item label="Asset Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={selectedAssetTypeIds}
                onChange={val => filter.setFilter("assetTypeId", val)}
                options={assetTypes?.map(a => ({ label: a.name, value: a.id }))}
              />
            </Form.Item>
          </Form>
        </Panel>

        <Panel header="Status & Type" key="statusType">
          <Form layout="vertical">
            <Form.Item label="Status">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.children ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter("statuses")}
                onChange={val => filter.setFilter("statuses", val)}
              >
                <Select.Option value={WorkOrderStatus.PendingApproval}>Pending Approval</Select.Option>
                <Select.Option value={WorkOrderStatus.Approved}>Approved</Select.Option>
                <Select.Option value={WorkOrderStatus.InProgress}>In Progress</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Type">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.children ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter("type")}
                onChange={val => filter.setFilter("type", val)}
              >
                <Select.Option value={WorkOrderType.Preventative}>Preventative</Select.Option>
                <Select.Option value={WorkOrderType.Corrective}>Corrective</Select.Option>
                <Select.Option value={WorkOrderType.Improvement}>Improvement</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Panel>

        {/* Assignment */}
        <Panel header="Assignment" key="assignment">
          <Form layout="vertical">
            <Form.Item label="Owner">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter("buildingLeadId")}
                onChange={val => filter.setFilter("buildingLeadId", val)}
                options={buildingLeads.map(u => ({ label: u.name, value: u.id }))}
              />
            </Form.Item>

            <Form.Item label="Technician">
              <Select
                mode="multiple"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filter.getFilter("technicianId")}
                onChange={val => filter.setFilter("technicianId", val)}
                options={technicians.map(u => ({ label: u.name, value: u.id }))}
              />
            </Form.Item>
          </Form>
        </Panel>
      </Collapse>
    </FilterContainer>
  );
}
